import { useCallback, useMemo, useState } from "react";
import axiosInstance, {
  setAxiosToken,
  isAxiosError,
} from "../../../../common/infrastructure/helpers/axiosHelper";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { keysToCamelCase } from "../../../../common/infrastructure/utilities/apiUtility";
import { downloadBlob } from "../../../../common/utilities/calendarUtility";
import { getErrorToast } from "../../../../common/infrastructure/utilities/toastUtility";
import { useAuthentication } from "../../../../common/infrastructure/hooks/useAuthentication";
import {
  INapAuditDocumentRow,
  INapAuditDocumentsResult,
  INapAuditPreviewResult,
} from "../models/napAuditPreviewModel";

// По подразбиране текущият месец и година.
const getInitialPeriod = (): { year: number; month: number } => {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 }; // getMonth() е 0-базиран
};

const extractErrorMessage = (err: unknown, fallback: string): string => {
  if (isAxiosError(err)) {
    const data = err.response?.data as unknown;
    if (typeof data === "string" && data.trim().length > 0) return data;
    if (data && typeof data === "object" && "title" in data) {
      return String((data as { title: unknown }).title);
    }
  }
  return fallback;
};

export const useNapAuditPanel = () => {
  const { currentUser } = useAuthentication();
  const initial = useMemo(getInitialPeriod, []);

  const [year, setYear] = useState<number>(initial.year);
  const [month, setMonth] = useState<number>(initial.month);

  const [rows, setRows] = useState<INapAuditDocumentRow[] | null>(null);
  const [docWarnings, setDocWarnings] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [result, setResult] = useState<INapAuditPreviewResult | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    return [current, current - 1, current - 2];
  }, []);

  // При смяна на периода нулирай заредените данни/резултата.
  const changeYear = useCallback((value: number) => {
    setYear(value);
    setRows(null);
    setResult(null);
  }, []);

  const changeMonth = useCallback((value: number) => {
    setMonth(value);
    setRows(null);
    setResult(null);
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    setResult(null);
    try {
      setAxiosToken(currentUser?.authToken);
      const url = `${endpointsMetadata.napAudit.documents}?year=${year}&month=${month}`;
      const response = await axiosInstance.get(url);
      const data = keysToCamelCase(response.data) as INapAuditDocumentsResult;
      setRows(data.rows ?? []);
      setDocWarnings(data.warnings ?? []);
    } catch (err) {
      setRows(null);
      getErrorToast(
        extractErrorMessage(err, "Възникна грешка при зареждането на данните."),
      );
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, year, month]);

  const updateRow = useCallback(
    (index: number, patch: Partial<INapAuditDocumentRow>) => {
      setRows((prev) => {
        if (!prev) return prev;
        const next = [...prev];
        next[index] = { ...next[index], ...patch };
        return next;
      });
    },
    [],
  );

  const buildPayload = useCallback(
    (list: INapAuditDocumentRow[]) => ({
      year,
      month,
      documents: list.map((r) => ({
        documentId: r.documentId,
        number: r.number,
        productName: r.productName,
        issueDate: r.issueDate,
        isExcluded: r.isExcluded,
      })),
    }),
    [year, month],
  );

  // Записва редакциите + авто‑преномериране; връща обновените редове или null при грешка.
  const save = useCallback(async (): Promise<INapAuditDocumentRow[] | null> => {
    if (!rows) return null;
    setIsSaving(true);
    try {
      setAxiosToken(currentUser?.authToken);
      const response = await axiosInstance.put(
        endpointsMetadata.napAudit.updateDocuments,
        buildPayload(rows),
      );
      const data = keysToCamelCase(response.data) as INapAuditDocumentsResult;
      setRows(data.rows ?? []);
      setDocWarnings(data.warnings ?? []);
      return data.rows ?? [];
    } catch (err) {
      getErrorToast(
        extractErrorMessage(err, "Възникна грешка при запазването на промените."),
      );
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [currentUser, rows, buildPayload]);

  const triggerDownload = useCallback((data: INapAuditPreviewResult) => {
    if (!data.canDownload || !data.fileBase64) return;

    const byteChars = atob(data.fileBase64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], {
      type: "application/xml",
    });
    downloadBlob(blob, data.fileName);
  }, []);

  // Първо запазва (авто‑преномериране), после генерира и автоматично сваля валидния файл.
  const generate = useCallback(async () => {
    const saved = await save();
    if (saved === null) return; // грешка при запис — не генерирай

    setIsGenerating(true);
    try {
      setAxiosToken(currentUser?.authToken);
      const url = `${endpointsMetadata.napAudit.generate}?year=${year}&month=${month}`;
      const response = await axiosInstance.get(url);
      const data = keysToCamelCase(response.data) as INapAuditPreviewResult;
      setResult(data);
      triggerDownload(data); // авто‑сваляне при валиден файл
    } catch (err) {
      setResult(null);
      getErrorToast(
        extractErrorMessage(err, "Възникна грешка при генерирането на файла."),
      );
    } finally {
      setIsGenerating(false);
    }
  }, [currentUser, year, month, save, triggerDownload]);

  const download = useCallback(() => {
    if (result) triggerDownload(result);
  }, [result, triggerDownload]);

  return {
    year,
    month,
    setYear: changeYear,
    setMonth: changeMonth,
    yearOptions,
    rows,
    docWarnings,
    isLoading,
    load,
    updateRow,
    isSaving,
    result,
    isGenerating,
    generate,
    download,
  };
};
